import {
  EXPLICIT_UNKNOWN_SOURCE_DISPLAY,
  Improvements,
  ORDER_FEATURES,
  OrderFeature,
  OrderFeatureDefinition,
  SelectedSources,
  isExplicitUnknownSelectedForFieldPath,
} from '@aloft/toolkit-shared';

import {
  ImprovementsPathPrefix,
  resolveOrderKeyedFeaturePdfCell,
} from '../../tkv2PDF/utils/utils.ts';

export type AdjustmentsFeatureDisplayContext = {
  isArv?: boolean;
};

const improvementsPathPrefix = (isArv?: boolean): ImprovementsPathPrefix =>
  isArv ? 'editableData.arv.improvements' : 'editableData.improvements';

export const resolveAdjustmentsFeatureDisplay = ({
  improvements,
  orderFeatures,
  selectedSources,
  isArv,
  orderKeys,
  featureDefs,
  unknownPathSuffixes,
  format,
}: {
  improvements?: Improvements;
  orderFeatures: OrderFeature[] | undefined;
  selectedSources?: SelectedSources;
  isArv?: boolean;
  orderKeys: readonly OrderFeature[];
  featureDefs: readonly OrderFeatureDefinition[];
  unknownPathSuffixes?: readonly string[];
  format: () => string;
}): string | undefined => {
  if (!improvements) return undefined;

  const prefix = improvementsPathPrefix(isArv);
  const result = resolveOrderKeyedFeaturePdfCell(improvements, orderFeatures, {
    orderKeys,
    featureDefs,
    unknownEarly: () =>
      unknownPathSuffixes?.some((suffix) =>
        isExplicitUnknownSelectedForFieldPath(selectedSources, `${prefix}.${suffix}`),
      )
        ? EXPLICIT_UNKNOWN_SOURCE_DISPLAY
        : undefined,
    format,
  });

  return result || undefined;
};

export const isBasementTreatAsNone = (
  improvements: Improvements | undefined,
  orderFeatures: OrderFeature[] | undefined,
): boolean => {
  if (!improvements) return false;
  return (
    improvements.basement?.hasFeature === false ||
    (orderFeatures !== undefined &&
      orderFeatures.includes('basement') &&
      improvements.basement?.hasFeature !== true)
  );
};

export const garageCarportFeatureDefs = [
  ORDER_FEATURES.carport,
  ORDER_FEATURES.detachedGarage,
  ORDER_FEATURES.garage,
] as const;

export const porchPatioDeckFeatureDefs = [
  ORDER_FEATURES.porch,
  ORDER_FEATURES.patio,
  ORDER_FEATURES.deck,
] as const;

export const poolSpaFeatureDefs = [ORDER_FEATURES.pool, ORDER_FEATURES.spa] as const;

export const shouldDisplayAdjustmentsFeature = ({
  featureDependencies,
  orderFeatures,
  improvementEntities,
}: {
  featureDependencies: readonly OrderFeature[];
  orderFeatures: OrderFeature[] | undefined;
  improvementEntities: Record<string, Improvements | undefined>;
}): boolean =>
  featureDependencies.some(
    (feature) =>
      orderFeatures?.includes(feature) ||
      Object.values(improvementEntities).some(
        (improvements) =>
          improvements != null &&
          (ORDER_FEATURES[feature].isPresent(improvements) ||
            ORDER_FEATURES[feature].hasFeature(improvements) ||
            ORDER_FEATURES[feature].isNone(improvements)),
      ),
  );
